import { computed, Injectable, signal } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { Movement } from '../models/movement.entity'
import {
  MovementField,
  MovementSearchParams,
} from '../models/movement-search.model'
import { Observable } from 'rxjs'
import { omit } from 'lodash'
import { WarehousesService } from '../../warehouses'
import { UsersService } from '../../users'
import { ReasonsService } from '../../reasons'
import { mergeById } from '../../../libs/filter.lib'
import {
  MovementsListingData,
  MovementsListingPage,
} from '../models/movement-listing.model'
import { GoodsReceivesService } from '../../goods-receives'
import { MovementsRepository } from '../movements.repository'

export interface MovementsListingState {
  data?: Movement[]
  extData?: MovementsListingData
  dropdownOptions?: MovementsListingData
  totalCount?: number
  searchString?: string
  fields?: MovementField[]
  filters?: MovementSearchParams
  searchKeys?: MovementSearchParams
  error?: HttpErrorResponse
  toReload: boolean
  isLoading: boolean
  isInitialized: boolean
}

const MOVEMENTS_INITIAL_STATE: MovementsListingState = {
  isLoading: false,
  isInitialized: false,
  toReload: false,
}

@Injectable()
export class MovementsListingStore {
  constructor(
    private movementsRepository: MovementsRepository,
    private goodsReceivesService: GoodsReceivesService,
    private reasonsService: ReasonsService,
    private usersService: UsersService,
    private warehousesService: WarehousesService,
  ) {}

  private state = signal<MovementsListingState>(MOVEMENTS_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly movements = computed(() => this.state().data || [])
  readonly extData = computed(() => this.state().extData)
  readonly dropdownOptions = computed(() => this.state().dropdownOptions)
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly searchKeys = computed(() => this.state().searchKeys)
  readonly fields = computed(() => this.state().fields)

  // Effects

  async search(
    params?: MovementSearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.movementsRepository
        .searchMovements$(
          this.parseStateFilterParams(this.state(), params),
          this.state().fields ?? [],
          this.state().extData,
          returnAll,
        )
        .subscribe({
          next: async (data) => {
            this.setData(data)
            resolve()
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  async searchLookup(event: {
    dataSourceKey: string
    q?: string
    dataKeyField?: string
    dataDisplayField?: string
    offset?: number
    limit?: number
  }): Promise<void> {
    type LookupKey = keyof MovementsListingData
    type ArrayElement<T> = T extends (infer U)[] ? U : never
    type LookupValue = ArrayElement<
      NonNullable<MovementsListingData[LookupKey]>
    >

    const queryKey = `${event.dataDisplayField ?? 'name'}:ct`
    const offset = event.offset ?? 0
    const limit = event.limit ?? 50
    const query = {
      offset,
      limit,
      ...(event.q ? { [queryKey]: event.q } : {}),
    } as Record<string, unknown>

    const serviceMap: Partial<
      Record<LookupKey, () => Observable<LookupValue[]>>
    > = {
      goodsReceives: () => this.goodsReceivesService.list$(query),
      reasons: () => this.reasonsService.list$(query),
      users: () => this.usersService.list$(query),
      warehouses: () => this.warehousesService.list$(query),
    }

    const search$ = serviceMap[event.dataSourceKey as LookupKey]
    if (!search$) return

    search$().subscribe((items) => {
      this.state.update((s) => {
        const key = event.dataSourceKey as LookupKey
        const prev = (s.dropdownOptions?.[key] ?? []) as { _id: string }[]
        const incoming = items as { _id: string }[]
        const merged = prev.length > 0 ? mergeById(prev, incoming) : incoming
        return {
          ...s,
          dropdownOptions: {
            ...s.extData,
            [event.dataSourceKey]: merged as LookupValue[],
          },
        }
      })
    })
  }

  // Reducers

  setData(data: MovementsListingPage) {
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      extData: data.extData,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: MovementSearchParams, keepFilters?: boolean) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters:
        keepFilters !== true
          ? { ...filters }
          : { ...this.filters(), ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: MovementSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  resetLookups() {
    this.state.set({
      ...this.state(),
      dropdownOptions: undefined,
      extData: undefined,
    })
  }

  removeFilter(filter: MovementSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof MovementSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  setFields(fields?: MovementField[]) {
    this.state.set({ ...this.state(), fields })
  }

  setSearchKeys(searchKeys?: MovementSearchParams) {
    this.state.set({ ...this.state(), searchKeys })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      searchString: undefined,
      filters: {},
    })
  }

  setTotalCount(totalCount: number) {
    this.state.set({
      ...this.state(),
      totalCount,
    })
  }

  private parseStateFilterParams(
    state: MovementsListingState,
    filters?: MovementSearchParams,
  ): MovementSearchParams {
    let params: MovementSearchParams = filters || {}
    if (state.searchString) {
      params.q = state.searchString
    }
    if (state.searchKeys) {
      params = { ...params, ...state.searchKeys }
    }
    if (filters?.sort) {
      params.sort = filters.sort
    }

    params = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => key !== undefined && value !== undefined,
      ),
    )

    return params
  }

  filterData(selected: Movement[] | undefined) {
    const state = this.state()

    if (selected && selected.length > 0) {
      return {
        params: {
          _id: selected.map((o) => o._id),
        },
        count: selected.length,
      }
    }

    return {
      params: omit(this.parseStateFilterParams(state), [
        'limit',
        'offset',
        'sort',
        'movement',
      ]),
      count: this.totalCount(),
    }
  }
}
